import React, { useState } from 'react';
import useMediaQuery from '@mui/system/useMediaQuery';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/joy';
import Dialog from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';
import { MenuBookRounded, OpenInNewRounded } from '@mui-symbols-material/w300';
import { BasicModalContent, LoadingScreen } from '@dreebit-registry/vsm-microservice-components';
import { useNetworkScanGuideUrl } from '../../hooks/useNetworkScanGuideUrl';

interface Props {
    open: boolean;
    onClose: () => void;
}

/**
 * Shows the static "OTTO Network Scan Kit" guide (public/help/network-scan-kit/)
 * inside the app: a tall dialog with the page in an iframe — embed mode hides
 * the page's own top bar — plus a jump to the full page in a new tab for
 * printing. See src/config/networkScan.ts.
 */
const NetworkScanGuideModal = ({ open, onClose }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const embedUrl = useNetworkScanGuideUrl({ embed: true });
    const pageUrl = useNetworkScanGuideUrl();
    const [loading, setLoading] = useState(true);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isFullScreen}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: isFullScreen ? undefined : { height: 'calc(100% - 64px)' } }}
            data-testid="network-scan-guide-modal"
        >
            <BasicModalContent
                startIcon={<MenuBookRounded />}
                onClose={onClose}
                style={{ height: '100%', minWidth: 0, boxSizing: 'border-box' }}
                title={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography level="titleSmallLight">{t('Network scan guide')}</Typography>
                        <Tooltip title={t('Open in new tab')} size="sm">
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                component="a"
                                href={pageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t('Open in new tab')}
                                data-testid="network-scan-guide-open"
                            >
                                <OpenInNewRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            >
                {/* Bleeds to the dialog edges — the page brings its own gutters. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        position: 'relative',
                        display: 'flex',
                        mt: '12px',
                        mx: '-16px',
                        mb: '-16px',
                        borderTop: '1px solid #ccc',
                    }}
                >
                    <iframe
                        src={embedUrl}
                        title={t('Network scan guide')}
                        onLoad={() => setLoading(false)}
                        style={{ flex: 1, width: '100%', border: 'none' }}
                        data-testid="network-scan-guide-frame"
                    />
                    {loading && <LoadingScreen style={{ position: 'absolute', inset: 0, backgroundColor: '#fff' }} />}
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default NetworkScanGuideModal;
